import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener, effect } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { SignedInUser } from '@app/schemas/user';
import { BYP_PLAN_GENERATED_LOCAL_KEY, NAVBAR_MENU_ITEMS, USER_RESUME_LOCAL_KEY } from '@app/utils/constants';
import { Store } from '@ngrx/store';
import { UserService, UserProfile } from '@app/services/api/user/user.service';
import { AuthService } from '@app/services/auth/auth.service';
import { tenantIdToRoute } from '@app/config/auth-constants';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { updateUserBiq } from '@app/state/actions/biq.action';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { analytics } from '@app/events/segment/analytics';
import { UserBiqData } from '@app/schemas/biq';
import { SurveyStorageService } from '@app/services/local-storage/survey-storage.service';

@Component({
  selector: 'app-navbar',
  imports: [
    CommonModule,
    RouterLink,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css'
})
export class NavbarComponent implements OnInit, OnDestroy {
  showMenu: boolean = false;
  userProfile: UserProfile | null = null;
  isDropdownOpen: boolean = false;
  menuRoutes: string[][] = [
    ['/career-matches', '/career-details'],
    ['/build-plan'],
    ['/find-job']
  ]
  menuItems = NAVBAR_MENU_ITEMS;
  public showMenuItems: boolean = false;
  public menuDisableRoutes = [
    '/user-info', '/survey', '/legal/terms', '/accessibility', '/terms'
  ];
  public userLoggedIn: boolean = false;
  private routerSubscription?: Subscription;
  private focusedIndex = 0;

  @ViewChild('dropdownTrigger') dropdownTriggerRef!: ElementRef<HTMLElement>;

  constructor(
    private router: Router,
    private store: Store<{ userBiq: UserBiqData }>,
    private userService: UserService,
    private authService: AuthService,
    private elementRef: ElementRef,
    private surveyStorageService: SurveyStorageService,
  ) {}

  ngOnInit() {
    this.loadUserProfile();
    this.setActiveMenuItem();
    
    // Subscribe to router events to update menu items on navigation
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.setActiveMenuItem();
      this.isDropdownOpen = false;
    });

    this.store.select('userBiq').subscribe(userBiq => {
      if (userBiq?.firstName && this.userProfile) {
        this.userProfile = {
          ...this.userProfile!,
          firstName: userBiq?.firstName,
          lastName: userBiq?.lastName,
        };
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.isDropdownOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  loadUserProfile() {
    this.userService.getMe().subscribe({
      next: (profile) => {
        this.userLoggedIn = true;

        this.userProfile = profile;
        if (profile.biq) {
          this.showMenu = true;
        } 
        const userBiq = profile.backgroundInfo;
        if (userBiq) {
          const ethnicity = userBiq.ethnicity.match(/"([^"]*)"/g)?.map(
            s => s.replace(/^"|"$/g, '').trim()
            ) || [];
          this.store.dispatch(updateUserBiq({ user: {
            firstName: userBiq.first_name,
            lastName: userBiq.last_name,
            location: userBiq.location,
            birthYear: userBiq.birth_year,
            gender: userBiq.gender,
            ethnicity: ethnicity,
            grade: userBiq.school_grade,
            graduationYear: userBiq.graduation_year,
            schoolName: userBiq.school_name,
            major: userBiq.graduation_major,
            isWorking: userBiq.is_working,
            jobType: userBiq.job_type ?? null,
            futureLocation: userBiq.future_location,
            futureLocationGooglePlaceId: userBiq.desired_location_google_place_id,
            futureLocationAdministrativeAreaLevel2: userBiq.desired_location_google_administrative_area_level_2,
          }}));
        }
      },
      error: (error) => {
        console.error('Failed to load user profile:', error);
      }
    });
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  navigateToProfile() {
    analytics.track('Account Navigate');
  }

  async signOut() {
    localStorage.removeItem(BYP_PLAN_GENERATED_LOCAL_KEY);
    localStorage.removeItem(USER_RESUME_LOCAL_KEY);
    localStorage.removeItem('user-biq-responses');
    localStorage.removeItem('user-biq-completed-steps');
    this.surveyStorageService.clearSurveyQuestions();
    this.surveyStorageService.clearUserResponses();
    analytics.track('User Sign Out');

    // Check if user is an SSO tenant and redirect accordingly
    const tenantId = this.authService.getCurrentUser()?.tenantId;
    const tenantRoute = tenantId ? tenantIdToRoute[tenantId] : null;

    if (tenantRoute) {
      // SSO user - call Amplify signOut and redirect to tenant-specific page
      await this.authService.signOut();
      window.location.href = `${window.location.origin}/${tenantRoute}`;
    } else {
      // Non-SSO user - use default logout
      window.location.href = `${window.location.origin}/auth/logout`;
    }
  }

  setActiveMenuItem() {
    const currentUrl = this.router.url;

    // Re-fetch profile if menu data isn't loaded yet (e.g. user just completed the survey)
    if (!this.showMenu) {
      this.loadUserProfile();
    }
    
    if (!this.menuDisableRoutes.find(route => currentUrl.startsWith(route))) {
      this.showMenuItems = true;
    } else {
      this.showMenuItems = false;
    }
    this.menuItems = this.menuItems.map((item, index) => {
      const isActive = this.menuRoutes[index].some(route => currentUrl.startsWith(route));
      return { ...item, active: isActive };
    });
  }

  changeTab(i: number) {
    const currentIndex = this.menuItems.findIndex(item => item.active);
 
    this.menuItems = this.menuItems.map((item, index) => {
      return { ...item, active: index === i ? true : false };
    });

    const menuItem = this.menuItems[i];
    const currentMenuItem = this.menuItems[currentIndex];
    if (menuItem) {
      analytics.track(menuItem.trackEvent, { section: currentMenuItem.section });
    }
  }

  openDropdownOption() {
    this.isDropdownOpen = true;
    this.focusedIndex = 0;
    setTimeout(() => this.setFocusOnItem(0));
  }

  setFocusOnItem(index: number) {
    const menuItems = document.querySelectorAll('.dropdown-item');
    const itemToFocus = menuItems[index] as HTMLElement;
    this.focusedIndex = index;
    if (itemToFocus) {
      itemToFocus.focus();
    }
  }

  selectItem(index: number) {
    if (index === 0) {
      this.router.navigate(['/profile']);
    } else if (index === 1) {
      this.signOut();
    }
    this.isDropdownOpen = false;
  }

  onSignOutKeydown(event: KeyboardEvent) {
    const isActivate = event.key === 'Enter' || event.key === ' ' || event.code === 'Space';
    if (isActivate) {
      event.preventDefault();
      this.signOut();
    }
  }

  onDropdownKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ' || event.code === 'Space') {
      event.preventDefault();
      if (this.isDropdownOpen) {
        this.closeDropdown();
      } else {
        this.openDropdownOption();
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.isDropdownOpen) {
        this.openDropdownOption();
      } else {
        this.setFocusOnItem(0);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDropdown();
    } else if (event.key === 'Tab' && this.isDropdownOpen) {
      this.closeDropdown();
    }
  }

  onDropdownItemKeydown(event: KeyboardEvent, index: number) {
    const itemCount = document.querySelectorAll('.dropdown-item').length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (index < itemCount - 1) {
        this.setFocusOnItem(index + 1);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index === 0) {
        this.closeDropdown();
      } else {
        this.setFocusOnItem(index - 1);
      }
    } else if (event.key === 'Enter' || event.key === ' ' || event.code === 'Space') {
      event.preventDefault();
      this.selectItem(index);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDropdown();
    } else if (event.key === 'Tab') {
      this.closeDropdown();
    }
  }

  private closeDropdown() {
    this.isDropdownOpen = false;
    this.dropdownTriggerRef?.nativeElement.focus();
  }

  private focusMainContent() {
    const main = document.getElementById('main-content');
    if (!main) return;
    main.setAttribute('tabindex', '-1');
    main.focus();
  }

  onSkipToMainClick(event: MouseEvent) {
    event.preventDefault();
    this.focusMainContent();
  }

  skipToMain(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ' || event.code === 'Space') {
      event.preventDefault();
      this.focusMainContent();
    }
  }

}
